import React, {useContext, useEffect, useMemo, useRef, useState} from 'react'
import { useIntl } from 'react-intl'
import PropTypes from 'prop-types'
import ReferenceSearch from './ReferenceSearch'
import ConnectorInfo from './ConnectorInfo'
import {ConnectorViewMappingContext} from './context/context'
import {Button, Card, Empty, Flex, Modal, Table} from 'antd'
import Radio from 'antd/es/radio'
import NoRecordsFound from '../../common/NoRecordsFound'
import RecordsCount from '../../common/RecordsCount'

const ReferenceSelector = (props) => {
  const intl = useIntl();
  const { connector, onClick, unmappedReferences } = props

  const [connVwMpConxt, connVwMpConxtDispatch] = useContext(
    ConnectorViewMappingContext
  )

  const svgLoaded = connVwMpConxt?.svgLoaded

  const [modal, contextHolder] = Modal.useModal()

  const listWrapperRef = useRef(null)
  const [tableBodyHeight, setTableBodyHeight] = useState(0)

  useEffect(() => {
    const el = listWrapperRef.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) => {
      setTableBodyHeight(entry.contentRect.height)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const [state, setState] = useState({
    selectedReference: '',
    includeSearch: '',
    excludeSearch: '',
  })

  const includeHandler = (e) => {
    setState({
      ...state,
      includeSearch: e.target.value.toUpperCase(),
    })
  }

  const excludeHandler = (e) => {
    setState({
      ...state,
      excludeSearch: e.target.value.toUpperCase(),
    })
  }

  const pasteToIncludeSearch = (arg) => {
    setState({
      ...state,
      includeSearch: arg,
    })
  }

  useEffect(() => {
    if (connVwMpConxt?.selected_reference) {
      executeScroll(connVwMpConxt?.selected_reference)
    }
  }, [connVwMpConxt?.selected_reference])

  useEffect(() => {
    if (connector.auto_mapped) {
      executeScroll(connector.auto_mapped)
    }
  }, [])

  const { includeSearch, excludeSearch } = state

  const references = useMemo(() => {
    let refs =
      connector.auto_mapped !== null && connector.reference
        ? [connector.reference, ...unmappedReferences]
        : [...unmappedReferences]
    if (includeSearch && includeSearch.trim()) {
      refs = refs.filter((ref) => ref.toUpperCase().includes(includeSearch))
    }
    if (excludeSearch && excludeSearch.trim()) {
      refs = refs.filter((ref) => !ref.toUpperCase().includes(excludeSearch))
    }
    return refs
  }, [unmappedReferences, connector.auto_mapped, connector.reference, includeSearch, excludeSearch])

  const executeScroll = (id) => {
    const divElement = document.getElementById(`ref-${id}`)
    if (divElement) {
      divElement.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const dataSource = useMemo(
    () =>
      references
        .sort()
        .map((ref, idx) => ({
          key: idx,
          reference: ref,
        })),
    [references]
  )

  const columns = useMemo(
    () => [
      {
        title: intl.formatMessage({id: 'connectorViews.mapping.threeDReference'}),
        dataIndex: 'reference',
        sorter: (a, b) => a.reference.localeCompare(b.reference),
        sortDirections: ['ascend', 'descend'],
        render: (text, record) => (
          <div
            className={`connector-mapping-ref-item${!svgLoaded ? ' disabled' : ''}`}
            id={`ref-${record.reference}`}
          >
            <div className="connector-mapping-ref-item-radio">
              <Radio
                checked={connVwMpConxt?.selected_reference === record.reference}
                disabled={!svgLoaded}
                onChange={() =>
                  connVwMpConxtDispatch({
                    type: 'selected_reference',
                    data: record.reference,
                  })
                }
              />
            </div>
            <div
              className="connector-mapping-ref-item-label"
              data-testid="vv-ref-item"
              onClick={() => {
                if (svgLoaded) {
                  connVwMpConxtDispatch({
                    type: 'selected_reference',
                    data: record.reference,
                  })
                }
              }}
            >
              {record.reference}
            </div>
          </div>
        ),
      },
    ],
    [connVwMpConxt?.selected_reference, svgLoaded]
  )

  return (
    <Flex vertical gap={12} className="connector-mapping-list-wrapper">
      {contextHolder}
      {/* Selected Connector */}
      <div className="connector-mapping-ref-connector-heading">
        <span className="connector-mapping-ref-card-title semi-bold">
          {intl.formatMessage({id: 'connectorViews.mapping.selectedConnector'})}
        </span>
        <Button
          type='link'
          className={`link-button connector-mapping-ref-clear${!svgLoaded ? ' disabled' : ''}`}
          disabled={!svgLoaded ? true : false}
          onClick={() => {
            if (!svgLoaded) return
            modal.confirm({
              title: intl.formatMessage({id: 'connectorViews.mapping.clearReferenceTitle'}),
              content: intl.formatMessage({id: 'connectorViews.mapping.clearReferenceContent'}),
              okText: intl.formatMessage({id: 'connectorViews.mapping.clear'}),
              okType: 'danger',
              centered: true,
              onOk: () => onClick(connector, null, false),
            })
          }}
        >
          {intl.formatMessage({id: 'connectorViews.mapping.clear'})}
        </Button>
      </div>
      <Card className="connector-mapping-ref-connector-card" data-testid="vv-selected-connector">
        <div className="connector-mapping-ref-card-field connector-mapping-ref-card-name">
          {connector.name}
        </div>
        <div className="connector-mapping-ref-card-field">
          {connector.description}
        </div>
        <div className="connector-mapping-ref-card-field">
          {intl.formatMessage({id: 'connectorViews.mapping.threeDReference'})}:{' '}
          {connector.svg_group_reference ? (
            <ConnectorInfo
              field={connector.svg_group_reference}
              onClick={() =>
                pasteToIncludeSearch(connector.svg_group_reference)
              }
            />
          ) : (
            '-'
          )}
        </div>
      </Card>

      {/* Select 3D Reference */}
      <div className="connector-mapping-ref-search-title semi-bold">
        {intl.formatMessage({id: 'connectorViews.mapping.select3DReference'})}
      </div>

      {/* Include Search */}
      <div className="connector-mapping-ref-search-field">
        <ReferenceSearch
          placeholder={intl.formatMessage({id: 'connectorViews.mapping.includeSearch'})}
          testId="vv-ref-include"
          value={includeSearch}
          onChange={(e) => includeHandler(e)}
        />
      </div>

      {/* Exclude Search */}
      <div className="connector-mapping-ref-search-field">
        <ReferenceSearch
          placeholder={intl.formatMessage({id: 'connectorViews.mapping.excludeSearch'})}
          testId="vv-ref-exclude"
          value={excludeSearch}
          onChange={(e) => excludeHandler(e)}
        />
      </div>

      {/* 3D Reference Table */}
      <div className="connector-mapping-ref-table-wrapper">
        <div className="connector-mapping-ref-table-content" ref={listWrapperRef}>
          <Table
            className="connector-mapping-ref-table"
            rowKey="key"
            columns={columns}
            dataSource={dataSource}
            pagination={false}
            size="small"
            scroll={{ y: tableBodyHeight - 36 }}
            locale={{
              emptyText: (
                <div style={{ padding: '40px' }}>
                  <Empty
                    description={ <RecordsCount count={0} /> }
                  />
                </div>
              ),
            }}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="connector-mapping-ref-actions">
        <Button
          block
          disabled={!svgLoaded}
          data-testid="vv-ref-cancel"
          onClick={() => {
            connVwMpConxtDispatch({
              type: 'select_mapping',
              data: {
                connector: {},
                svg_group_reference: null,
                selected_reference: null,
              },
            })
          }}
        >
          {intl.formatMessage({id: 'forms.cancel'})}
        </Button>
        <Button
          type="primary"
          block
          disabled={!svgLoaded}
          data-testid="vv-ref-apply"
          onClick={() =>
            onClick(connector, connVwMpConxt?.selected_reference, true)
          }
        >
          {intl.formatMessage({id: 'connectorViews.mapping.apply'})}
        </Button>
      </div>
    </Flex>
  )
}

ReferenceSelector.propTypes = {
  connector: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  unmappedReferences: PropTypes.array.isRequired,
}

export default ReferenceSelector
